import React, { useState } from 'react';
import { Image as ImageIcon, UserCircle, Building, Link, Video, Music, Plus, Trash2, X } from 'lucide-react';
import StarRating from './StarRating';
import ImageCropper from '../Image/ImageCropper';
import { useLanguage } from '../../hooks/useLanguage';

// アニメ編集フォーム
const AnimeEditForm = ({ anime, onSave, onCancel, uploadImage, suggestions }) => {
  const { t } = useLanguage();
  const [editedAnime, setEditedAnime] = useState({
    startEpisode: anime.startEpisode ?? 1,
    ...anime,
    // 既存データの互換性のため
    opSongs: anime.opSongs || (anime.opSong ? [anime.opSong] : [{ title: '', artist: '', rating: 0 }]),
    edSongs: anime.edSongs || (anime.edSong ? [anime.edSong] : [{ title: '', artist: '', rating: 0 }]),
    favoriteCharacters: anime.favoriteCharacters || [],
    ratingMode: anime.ratingMode || 'simple'
  });
  const [episodeCountInput,setEpisodeCountInput] = useState(String(anime.episodes.length));
  
  const [showSuggestions, setShowSuggestions] = useState({});
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperCallback, setCropperCallback] = useState(null);
  const [cropperType, setCropperType] = useState(null); // 'thumbnail' or 'character'
  const [selectedFileName, setSelectedFileName] = useState(null);

  // 開始話数入力欄用（文字列をそのまま保持）
  const [startEpisodeInput, setStartEpisodeInput] = useState(
    String(editedAnime.startEpisode)
  );

  // ファイル選択処理
  const handleImageSelect = (e, type, index = null) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      setCropperImage(event.target.result);
      setCropperType(type);
      setCropperCallback(() => async (croppedBlob) => {
        try {
          if (type === 'thumbnail') {
            const url = await uploadImage(croppedBlob, `anime/${anime.id}/thumbnail.jpg`);
            setEditedAnime({ ...editedAnime, thumbnail: url });
          } else if (type === 'character') {
            const url = await uploadImage(croppedBlob, `anime/${anime.id}/character${index}.jpg`);
            const newCharacters = [...editedAnime.favoriteCharacters];
            if (!newCharacters[index]) {
              newCharacters[index] = { name: '', voiceActor: '', image: null };
            }
            newCharacters[index].image = url;
            setEditedAnime({ ...editedAnime, favoriteCharacters: newCharacters });
          }
        } catch (error) {
          console.error(t('uploadFailed') + ':', error);
          alert(t('uploadFailed'));
        }
        setShowCropper(false);
        setSelectedFileName(null);
      });
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  // キャラクター追加
  const addCharacter = () => {
    if (editedAnime.favoriteCharacters.length >= 3) {
      alert(t('maxCharactersLimit'));
      return;
    }
    setEditedAnime({
      ...editedAnime,
      favoriteCharacters: [...editedAnime.favoriteCharacters, { name: '', voiceActor: '', image: null }]
    });
  };

  // キャラクター削除
  const removeCharacter = (index) => {
    const newCharacters = editedAnime.favoriteCharacters.filter((_, i) => i !== index);
    setEditedAnime({ ...editedAnime, favoriteCharacters: newCharacters });
  };

  // 主題歌追加
  const addSong = (type) => {
    const key = type === 'op' ? 'opSongs' : 'edSongs';
    setEditedAnime({
      ...editedAnime,
      [key]: [...editedAnime[key], { title: '', artist: '', rating: 0 }]
    });
  };

  // 主題歌削除
  const removeSong = (type, index) => {
    const key = type === 'op' ? 'opSongs' : 'edSongs';
    const newSongs = editedAnime[key].filter((_, i) => i !== index);
    setEditedAnime({ ...editedAnime, [key]: newSongs });
  };

  // 主題歌更新
  const updateSong = (type, index, field, value) => {
    const key = type === 'op' ? 'opSongs' : 'edSongs';
    const newSongs = [...editedAnime[key]];
    newSongs[index] = { ...newSongs[index], [field]: value };
    setEditedAnime({ ...editedAnime, [key]: newSongs });
  };

  const getSuggestionsForField = (field) => {
    const value = editedAnime[field] || '';
    return suggestions[field]?.filter(s =>
      s.toLowerCase().includes(value.toLowerCase())
    ) || [];
  };

  const handleSave = () => {
    // エピソードに評価モードを反映
    const updatedEpisodes = editedAnime.episodes.map(ep => ({
      ...ep,
      detailedRating: ep.detailedRating || { content: 0, animation: 0, sound: 0 }
    }));
    
    onSave({
      ...editedAnime,
      episodes: updatedEpisodes
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto overflow-x-hidden">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-4xl my-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold dark:text-gray-100">{t('editAnime')}</h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {showCropper && (
          <ImageCropper
            imageUrl={cropperImage}
            onCrop={cropperCallback}
            onCancel={() => {
              setShowCropper(false);
              setSelectedFileName(null);
            }}
            aspectRatio={cropperType === 'thumbnail' ? 16/9 : 1}
            circularCrop={cropperType === 'character'}
          />
        )}

        <div className="space-y-6 max-h-[70vh] overflow-y-auto overflow-x-hidden pr-2">
          {/* 基本情報 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('title')}</label>
              <input
                type="text"
                value={editedAnime.title}
                onChange={(e) => setEditedAnime({ ...editedAnime, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <label className="block text-sm font-medium text-gray-700 mb-2 mt-2">{t('subtitle')}</label>
              <input
                type="text"
                value={editedAnime.subtitle || ''}
                onChange={e => setEditedAnime({ ...editedAnime, subtitle: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('ratingMode')}</label>
              <select
                value={editedAnime.ratingMode}
                onChange={(e) => setEditedAnime({ ...editedAnime, ratingMode: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="simple">{t('simpleRating')} ({t('simpleRatingDesc')})</option>
                <option value="detailed">{t('detailedRating')} ({t('content')}・{t('animation')}・{t('sound')})</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('broadcastYear')}</label>
              <input
                type="number"
                value={editedAnime.year}
                onChange={(e) => setEditedAnime({ ...editedAnime, year: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('season')}</label>
              <select
                value={editedAnime.season || ''}
                onChange={(e) => setEditedAnime({ ...editedAnime, season: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('noSelection')}</option>
                <option value="春">{t('springCour')}</option>
                <option value="夏">{t('summerCour')}</option>
                <option value="秋">{t('autumnCour')}</option>
                <option value="冬">{t('winterCour')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('totalEpisodes')}</label>
              <input
                type="number"
                min="1"
                value={episodeCountInput}
                onChange={e => setEpisodeCountInput(e.target.value)}   // 文字列で保持
                onBlur={() => {
                  // 入力を確定したタイミングで episodes 配列を再構築
                  if (episodeCountInput.trim() === '') return; // 空の場合は変更しない
                  const newCount = Math.max(1, parseInt(episodeCountInput, 10));
                  const currentCount = editedAnime.episodes.length;
                  let newEpisodes = [...editedAnime.episodes];

                  if (newCount > currentCount) {
                    for (let i = currentCount; i < newCount; i++) {
                      newEpisodes.push({
                        id: `${Date.now()}-${i}`,
                        number: (editedAnime.startEpisode ?? 1) + i,
                        title: `第${(editedAnime.startEpisode ?? 1) + i}話`,
                        storyTitle: '',
                        rating: 0,
                        detailedRating: { content: 0, animation: 0, sound: 0 },
                        notes: '',
                        watchedDate: null
                      });
                    }
                  } else {
                    newEpisodes = newEpisodes.slice(0, newCount);
                  }

                  setEditedAnime({ ...editedAnime, episodes: newEpisodes });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('startEpisodeNum')}</label>
              {/* 入力中は文字列で保持し、onBlur で確定する方式 */}
              <input
                type="number"
                min="0"
                value={startEpisodeInput}
                onChange={e => setStartEpisodeInput(e.target.value)}
                onBlur={() => {
                  if (startEpisodeInput.trim() === '') return; // 空入力は無視
                  const start = Math.max(0, parseInt(startEpisodeInput, 10));
                  const updatedEpisodes = editedAnime.episodes.map((ep, idx) => ({
                    ...ep,
                    number: start + idx,
                    title: t('episode', { number: start + idx })
                  }));
                  setEditedAnime({
                    ...editedAnime,
                    startEpisode: start,
                    episodes: updatedEpisodes
                  });
                  setStartEpisodeInput(String(start));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('broadcastDay')}</label>
              <input
                type="text"
                value={editedAnime.broadcastDay || ''}
                onChange={(e) => setEditedAnime({ ...editedAnime, broadcastDay: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={t('example') + ': ' + t('broadcastDayExample')}
              />
            </div>
          </div>

          {/* サムネイル画像 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <ImageIcon size={16} className="inline mr-1" />
              {t('thumbnailImage')}
            </label>
            <div className="flex items-center gap-4">
              {editedAnime.thumbnail && (
                <div className="relative group">
                  <img 
                    src={editedAnime.thumbnail} 
                    alt="サムネイル" 
                    className="h-24 rounded-lg object-cover" 
                  />
                  <button
                    onClick={() => setEditedAnime({ ...editedAnime, thumbnail: null })}
                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageSelect(e, 'thumbnail')}
                  className="hidden"
                />
                <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-block">
                  {t('selectImage')}
                </span>
              </label>
            </div>
          </div>

          {/* 推しキャラクター */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <UserCircle size={16} className="inline mr-1" />
              {t('favoriteCharacters')} ({t('maxCharacters')})
            </label>
            <div className="space-y-4">
              {editedAnime.favoriteCharacters.map((char, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder={t('characterName')}
                        value={char.name || ''}
                        onChange={(e) => {
                          const chars = [...editedAnime.favoriteCharacters];
                          chars[index] = { ...chars[index], name: e.target.value };
                          setEditedAnime({ ...editedAnime, favoriteCharacters: chars });
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder={t('voiceActor') + ' (' + t('optional') + ')'}
                        value={char.voiceActor || ''}
                        onChange={(e) => {
                          const chars = [...editedAnime.favoriteCharacters];
                          chars[index] = { ...chars[index], voiceActor: e.target.value };
                          setEditedAnime({ ...editedAnime, favoriteCharacters: chars });
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {char.image && (
                        <img 
                          src={char.image} 
                          alt={char.name} 
                          className="h-12 w-12 rounded-full object-cover" 
                        />
                      )}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect(e, 'character', index)}
                          className="hidden"
                        />
                        <span className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors inline-block">
                          <ImageIcon size={16} />
                        </span>
                      </label>
                      <button
                        onClick={() => removeCharacter(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {editedAnime.favoriteCharacters.length < 3 && (
                <button
                  onClick={addCharacter}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <Plus size={16} />
                  {t('addCharacter')}
                </button>
              )}
            </div>
          </div>

          {/* 配信・制作情報 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('streamingSite')}</label>
              <input
                type="text"
                value={editedAnime.streamingSite || ''}
                onChange={(e) => setEditedAnime({ ...editedAnime, streamingSite: e.target.value })}
                onFocus={() => setShowSuggestions({ ...showSuggestions, streamingSite: true })}
                onBlur={() => setTimeout(() => setShowSuggestions({ ...showSuggestions, streamingSite: false }), 200)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={t('example') + ': ' + t('streamingSiteExample')}
              />
              {showSuggestions.streamingSite && getSuggestionsForField('streamingSite').length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                  {getSuggestionsForField('streamingSite').map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => setEditedAnime({ ...editedAnime, streamingSite: suggestion })}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building size={16} className="inline mr-1" />
                {t('studio')}
              </label>
              <input
                type="text"
                value={editedAnime.studio || ''}
                onChange={(e) => setEditedAnime({ ...editedAnime, studio: e.target.value })}
                onFocus={() => setShowSuggestions({ ...showSuggestions, studio: true })}
                onBlur={() => setTimeout(() => setShowSuggestions({ ...showSuggestions, studio: false }), 200)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={t('example') + ': ' + t('studioExample')}
              />
              {showSuggestions.studio && getSuggestionsForField('studio').length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                  {getSuggestionsForField('studio').map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => setEditedAnime({ ...editedAnime, studio: suggestion })}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('director')}</label>
              <input
                type="text"
                value={editedAnime.director || ''}
                onChange={(e) => setEditedAnime({ ...editedAnime, director: e.target.value })}
                onFocus={() => setShowSuggestions({ ...showSuggestions, director: true })}
                onBlur={() => setTimeout(() => setShowSuggestions({ ...showSuggestions, director: false }), 200)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={t('example') + ': ' + t('directorExample')}
              />
              {showSuggestions.director && getSuggestionsForField('director').length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                  {getSuggestionsForField('director').map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => setEditedAnime({ ...editedAnime, director: suggestion })}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* リンク */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Link size={16} className="inline mr-1" />
                {t('officialWebsite')}
              </label>
              <input
                type="url"
                value={editedAnime.officialSite || ''}
                onChange={(e) => setEditedAnime({ ...editedAnime, officialSite: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Video size={16} className="inline mr-1" />
                {t('previewVideo')}
              </label>
              <input
                type="url"
                value={editedAnime.pvUrl || ''}
                onChange={(e) => setEditedAnime({ ...editedAnime, pvUrl: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://www.youtube.com/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Link size={16} className="inline mr-1" />
                {t('officialTwitter')}
              </label>
              <input
                type="url"
                placeholder="https://x.com/official"
                value={editedAnime.officialX || ''}
                onChange={e=>setEditedAnime({...editedAnime, officialX:e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* 主題歌 */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Music size={16} className="inline mr-1" />
                {t('openingTheme')}
              </label>
              <div className="space-y-3">
                {editedAnime.opSongs.map((song, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input
                        type="text"
                        placeholder={t('songTitle')}
                        value={song.title || ''}
                        onChange={(e) => updateSong('op', index, 'title', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder={t('artist')}
                        value={song.artist || ''}
                        onChange={(e) => updateSong('op', index, 'artist', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">{t('rating')}:</span>
                          <StarRating
                            rating={song.rating || 0}
                            size={20}
                            interactive={true}
                            onRatingChange={(rating) => updateSong('op', index, 'rating', rating)}
                          />
                        </div>
                        {editedAnime.opSongs.length > 1 && (
                          <button
                            onClick={() => removeSong('op', index)}
                            className="text-red-600 hover:bg-red-50 p-1 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => addSong('op')}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <Plus size={16} />
                  {t('addOP')}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Music size={16} className="inline mr-1" />
                {t('endingTheme')}
              </label>
              <div className="space-y-3">
                {editedAnime.edSongs.map((song, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input
                        type="text"
                        placeholder={t('songTitle')}
                        value={song.title || ''}
                        onChange={(e) => updateSong('ed', index, 'title', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder={t('artist')}
                        value={song.artist || ''}
                        onChange={(e) => updateSong('ed', index, 'artist', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">{t('rating')}:</span>
                          <StarRating
                            rating={song.rating || 0}
                            size={20}
                            interactive={true}
                            onRatingChange={(rating) => updateSong('ed', index, 'rating', rating)}
                          />
                        </div>
                        {editedAnime.edSongs.length > 1 && (
                          <button
                            onClick={() => removeSong('ed', index)}
                            className="text-red-600 hover:bg-red-50 p-1 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => addSong('ed')}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <Plus size={16} />
                  {t('addED')}
                </button>
              </div>
            </div>
          </div>

          {/* 視聴ステータス */}
          {editedAnime.dropped && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-red-700 font-medium">
                  {t('droppedAt', { number: editedAnime.droppedAtEpisode })}
                </span>
                <button
                  onClick={() => setEditedAnime({ ...editedAnime, dropped: false, droppedAtEpisode: null })}
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                >
                  {t('reviveFromDrop')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6 pt-6 border-t">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnimeEditForm;
